/**
 * Upload recorded segments while the meeting is still running.
 *
 * The queue exists because uploading is the one part of recording that is allowed
 * to fail. A tunnel, a flaky hotel wifi, a laptop that slept — none of those should
 * cost the meeting, so a segment that will not upload is kept and retried, and the
 * recorder is never blocked waiting for the network.
 *
 * Guarantees:
 *
 * - **One chunk stays one chunk.** Every segment carries a client-generated UUID
 *   that the server uses as its idempotency key, so a retry of a request whose
 *   response was lost is recognised as the same chunk rather than stored twice.
 * - **Order is preserved on the wire but not required.** Chunks are sent one at a
 *   time in sequence order, which keeps the server's received-count monotonic; the
 *   server orders by sequence number regardless, so an out-of-order arrival after a
 *   retry is still assembled correctly.
 * - **Nothing is dropped silently.** `pending` only reaches zero when every
 *   segment has been acknowledged, and `flush()` is what the caller awaits before
 *   finalizing.
 */

import type { MeetlyChunkUpload } from '../api/meetly';
import type { RecordedSegment } from './meetlySegmentedRecorder';
import { recorderMimeToExtension } from './meetlyAudio';

export type ChunkUploader = (chunk: MeetlyChunkUpload) => Promise<{ duplicate: boolean }>;

export interface ChunkQueueEvents {
  onProgress?: (state: ChunkQueueState) => void;
  /** Fired when a chunk has failed every attempt and is being kept for later. */
  onStalled?: (state: ChunkQueueState) => void;
}

export interface ChunkQueueState {
  uploaded: number;
  pending: number;
  failed: number;
  /** True while the queue is actively retrying rather than making progress. */
  retrying: boolean;
  lastError: string | null;
}

export interface ChunkQueueOptions extends ChunkQueueEvents {
  upload: ChunkUploader;
  /** Attempts per chunk before it is parked for the next flush. */
  maxAttempts?: number;
  baseDelayMs?: number;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

interface QueuedChunk extends MeetlyChunkUpload {
  attempts: number;
}

const newChunkUuid = () =>
  globalThis.crypto?.randomUUID?.()
  ?? `chunk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export class MeetlyChunkQueue {
  private readonly queue: QueuedChunk[] = [];
  private readonly options: ChunkQueueOptions;
  private uploaded = 0;
  private failed = 0;
  private retrying = false;
  private lastError: string | null = null;
  private draining = false;
  private disposed = false;
  private idle: Array<() => void> = [];

  constructor(options: ChunkQueueOptions) {
    this.options = options;
  }

  get state(): ChunkQueueState {
    return {
      uploaded: this.uploaded,
      pending: this.queue.length,
      failed: this.failed,
      retrying: this.retrying,
      lastError: this.lastError,
    };
  }

  private emit() {
    this.options.onProgress?.(this.state);
  }

  /** Accept a segment from the recorder. Never throws, never blocks. */
  enqueue(segment: RecordedSegment) {
    if (this.disposed) return;
    this.queue.push({
      blob: segment.blob,
      chunkIndex: segment.index,
      chunkUuid: newChunkUuid(),
      startTime: Number(segment.startTime.toFixed(3)),
      endTime: Number(segment.endTime.toFixed(3)),
      duration: Number(segment.duration.toFixed(3)),
      mimeType: segment.mimeType,
      extension: recorderMimeToExtension(segment.mimeType),
      attempts: 0,
    });
    this.emit();
    void this.drain();
  }

  private async drain(): Promise<void> {
    if (this.draining || this.disposed) return;
    this.draining = true;
    try {
      while (this.queue.length > 0 && !this.disposed) {
        const chunk = this.queue[0];
        try {
          await this.options.upload(chunk);
          this.queue.shift();
          this.uploaded += 1;
          this.retrying = false;
          this.lastError = null;
          this.emit();
        } catch (error) {
          chunk.attempts += 1;
          this.lastError = describeUploadError(error);
          const maxAttempts = this.options.maxAttempts ?? 5;

          if (isPermanentRejection(error)) {
            // The server will never take this chunk (it is already past uploads, or
            // the payload is malformed). Keeping it in the queue would block every
            // chunk behind it for the rest of the meeting.
            this.queue.shift();
            this.failed += 1;
            this.retrying = false;
            this.emit();
            continue;
          }

          if (chunk.attempts >= maxAttempts) {
            // Park the whole queue rather than dropping the chunk: the audio still
            // exists, and flush() at the end of the meeting gets another run at it.
            this.retrying = false;
            this.emit();
            this.options.onStalled?.(this.state);
            return;
          }

          this.retrying = true;
          this.emit();
          const base = this.options.baseDelayMs ?? 1500;
          await (this.options.sleep ?? defaultSleep)(
            Math.min(base * 2 ** (chunk.attempts - 1), 15_000),
          );
        }
      }
    } finally {
      this.draining = false;
      // Wake flush() whenever draining stops, not only when the queue empties. A
      // stalled queue still has chunks in it, and waiting for it to empty would
      // mean a user on a dead network could never finish their meeting — the one
      // situation where ending it promptly matters most.
      const waiters = this.idle;
      this.idle = [];
      waiters.forEach(resolve => resolve());
    }
  }

  /**
   * Push everything still queued, resetting attempt counters first.
   *
   * Called when the meeting ends: by then the network may well have recovered, and
   * a chunk parked ten minutes ago deserves a fresh budget rather than inheriting
   * the exhausted one.
   */
  async flush(): Promise<ChunkQueueState> {
    if (this.disposed) return this.state;
    this.queue.forEach(chunk => { chunk.attempts = 0; });
    const settled = this.queue.length === 0
      ? Promise.resolve()
      : new Promise<void>(resolve => this.idle.push(resolve));
    void this.drain();
    await settled;
    return this.state;
  }

  /** Chunks still held because they could not be uploaded. */
  get stalledCount(): number {
    return this.queue.length;
  }

  dispose() {
    this.disposed = true;
    this.queue.length = 0;
    const waiters = this.idle;
    this.idle = [];
    waiters.forEach(resolve => resolve());
  }
}

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * 4xx other than 408/429 means re-sending the identical bytes cannot succeed.
 * Retrying them burns the attempt budget and, worse, holds back every later chunk.
 */
export const isPermanentRejection = (error: unknown): boolean => {
  const status = statusOf(error);
  if (status === undefined) return false;
  if (status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
};

export const describeUploadError = (error: unknown): string => {
  const status = statusOf(error);
  if (status === 409) return 'This part of the recording was already saved.';
  if (status === 413) return 'That part of the recording was too large to send.';
  if (status && status >= 500) return 'Meetly is busy. Retrying automatically.';
  return 'Waiting for network. Retrying automatically.';
};

export default MeetlyChunkQueue;
